import React, { useState, useEffect } from 'react';
import {
  Button,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Tabs,
  Tag,
  Space,
  Popconfirm,
  message,
  Card,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BookOutlined,
  SafetyOutlined,
  ToolOutlined,
  ExperimentOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import {
  create_training_course,
  get_training_courses,
  update_training_course,
  delete_training_course,
  create_operation_requirement,
  get_operation_requirements,
} from '../../../utils/training_api';
import { bp_get_operation_type } from '../../../utils/api';
import StatCard from '@/components/StatCard';
import DataTable from '@/components/DataTable';
import PageHeader, { EmptyState } from '@/components/PageHeader';
import { BookOpen, CheckCircle, Settings, Award } from 'lucide-react';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import { useTranslation } from 'react-i18next';
import PageModal from '@/components/PageModal';

const { Option } = Select;
const { TextArea } = Input;

const TrainingConfiguration = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();

  // State for Training Courses
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [courseModalVisible, setCourseModalVisible] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('1');   // which tab is open — its Add button sits in the page header

  // State for Operation Requirements
  const [requirements, setRequirements] = useState([]);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [requirementModalVisible, setRequirementModalVisible] = useState(false);
  const [operations, setOperations] = useState([]);
  const [requirementForm] = Form.useForm();

  // Statistics
  const [stats, setStats] = useState({
    totalCourses: 0,
    activeCourses: 0,
    operationsWithRequirements: 0,
  });

  // Search states

  // Training type options
  const trainingTypes = [
    { value: 'GMP', label: 'GMP (Good Manufacturing Practice)', icon: <SafetyOutlined /> },
    { value: 'SOP', label: 'SOP (Standard Operating Procedure)', icon: <FileTextOutlined /> },
    { value: 'EQUIPMENT', label: 'Equipment Training', icon: <ToolOutlined /> },
    { value: 'PRODUCT', label: 'Product Training', icon: <ExperimentOutlined /> },
    { value: 'SAFETY', label: 'Safety Training', icon: <SafetyOutlined /> },
    { value: 'QUALITY', label: 'Quality Training', icon: <BookOutlined /> },
    { value: 'OTHER', label: 'Other', icon: <FileTextOutlined /> },
  ];

  useEffect(() => {
    fetchCourses();
    fetchRequirements();
    fetchOperations();
  }, []);

  // Fetch training courses
  const fetchCourses = async () => {
    setCoursesLoading(true);
    try {
      await get_training_courses(null, (res) => {
        if (res?.status === 'success') {
          setCourses(res.data || []);
          updateStats(res.data || []);
        }
      });
    } catch (error) {
      message.error('Failed to fetch training courses');
    } finally {
      setCoursesLoading(false);
    }
  };

  // Fetch operation requirements
  const fetchRequirements = async () => {
    setRequirementsLoading(true);
    try {
      await get_operation_requirements(null, (res) => {
        if (res?.status === 'success') {
          setRequirements(res.data || []);
        }
      });
    } catch (error) {
      message.error('Failed to fetch operation requirements');
    } finally {
      setRequirementsLoading(false);
    }
  };

  // Fetch operation types
  const fetchOperations = async () => {
    try {
      await bp_get_operation_type((res) => {
        console.log('API Response:', res);
        console.log('API Data:', res?.data);
        if (res?.data?.status === 'success') {
          const rawData = res.data.data || [];
          console.log('Raw operation types count:', rawData.length);
          console.log('First 5 operations:', rawData.slice(0, 5));

          // Map operation types to expected format with new comprehensive fields
          const mapped = rawData.map((op) => ({
            operation_code: op.code || op.operation_type, // Use code field, fallback to operation_type
            operation_name: op.operation_type,
            id: op.operation_type_id,
            category: op.category,
            description: op.description,
            is_critical: op.is_critical,
            required_equipment_type: op.required_equipment_type,
            typical_duration_hours: op.typical_duration_hours,
          }));
          console.log('Mapped operations count:', mapped.length);
          console.log('First 5 mapped:', mapped.slice(0, 5));
          setOperations(mapped);
        } else {
          console.error('API returned non-success status:', res?.data?.status);
        }
      });
    } catch (error) {
      console.error('Failed to fetch operation types:', error);
    }
  };

  // Update statistics
  const updateStats = (coursesData) => {
    const activeCourses = coursesData.filter((c) => c.is_active).length;
    const uniqueOperations = new Set(requirements.map((r) => r.operation_code)).size;

    setStats({
      totalCourses: coursesData.length,
      activeCourses,
      operationsWithRequirements: uniqueOperations,
    });
  };

  // Handle create/update course
  const handleSaveCourse = async () => {
    try {
      const values = await courseForm.validateFields();

      if (editingCourse) {
        await update_training_course({ ...values, course_id: editingCourse.id }, (res) => {
          if (res?.status === 'success') {
            message.success('Training course updated successfully');
            setCourseModalVisible(false);
            courseForm.resetFields();
            setEditingCourse(null);
            fetchCourses();
          }
        });
      } else {
        await create_training_course(values, (res) => {
          if (res?.status === 'success') {
            message.success('Training course created successfully');
            setCourseModalVisible(false);
            courseForm.resetFields();
            fetchCourses();
          }
        });
      }
    } catch (error) {
      message.error('Failed to save training course');
    }
  };

  // Handle delete course
  const handleDeleteCourse = async (courseId) => {
    try {
      await delete_training_course(courseId, (res) => {
        if (res?.status === 'success') {
          message.success('Training course deleted successfully');
          fetchCourses();
        }
      });
    } catch (error) {
      message.error('Failed to delete training course');
    }
  };

  // Handle edit course
  const handleAddCourse = () => {
    setEditingCourse(null);
    courseForm.resetFields();
    setCourseModalVisible(true);
  };

  const handleAddRequirement = () => {
    requirementForm.resetFields();
    setRequirementModalVisible(true);
  };

  const handleEditCourse = (course) => {
    setEditingCourse(course);
    courseForm.setFieldsValue({
      course_code: course.course_code,
      course_name: course.course_name,
      training_type: course.training_type,
      description: course.description,
      duration_hours: course.duration_hours,
      validity_months: course.validity_months,
      is_active: course.is_active,
      requires_assessment: course.requires_assessment,
      passing_score: course.passing_score,
      training_material_url: course.training_material_url,
    });
    setCourseModalVisible(true);
  };

  // Handle add operation requirement
  const handleSaveRequirement = async () => {
    try {
      const values = await requirementForm.validateFields();
      console.log('Form values:', values);

      // Validate that operations array is not empty
      if (!operations || operations.length === 0) {
        message.error('No operations available. Please refresh the page.');
        return;
      }

      // Find the selected operation to get operation_name
      const selectedOperation = operations.find((op) => op.operation_code === values.operation_code);
      console.log('Selected operation:', selectedOperation);

      if (!selectedOperation) {
        message.error('Selected operation not found. Please try again.');
        return;
      }

      // Prepare data with operation_name included
      const requestData = {
        operation_code: values.operation_code,
        course_id: values.course_id,
        operation_name: selectedOperation.operation_name || values.operation_code,
        is_mandatory: values.is_mandatory !== undefined ? values.is_mandatory : true,
        customer_id: 1,
      };

      console.log('Sending request data:', requestData);

      await create_operation_requirement(requestData, (res) => {
        console.log('API Response:', res);
        if (res?.status === 'success') {
          message.success(res.message || 'Operation requirement created successfully');
          setRequirementModalVisible(false);
          requirementForm.resetFields();
          fetchRequirements();
        } else {
          message.error(res?.message || 'Failed to create operation requirement');
        }
      });
    } catch (error) {
      console.error('Error saving requirement:', error);
      if (error.errorFields) {
        message.error('Please fill in all required fields');
      } else {
        message.error(error?.message || 'Failed to save operation requirement');
      }
    }
  };

  // Training Courses Table Columns
  const allCourseColumns = [
    {
      title: 'Course Code',
      dataIndex: 'course_code',
      key: 'course_code',
      width: 150,
      defaultVisible: true,
      sortType: 'string',
      searchable: true,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Course Name',
      dataIndex: 'course_name',
      key: 'course_name',
      width: 320,
      defaultVisible: true,
      sortType: 'string',
      searchable: true,
    },
    {
      title: 'Type',
      dataIndex: 'training_type',
      key: 'training_type',
      width: 160,
      defaultVisible: true,
      sortType: 'string',
      searchable: true,
      render: (type) => {
        const typeConfig = trainingTypes.find((t) => t.value === type);
        return (
          <Tag>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{typeConfig?.icon}{type}</span>
          </Tag>
        );
      },
    },
    {
      title: 'Duration (hrs)',
      dataIndex: 'duration_hours',
      key: 'duration_hours',
      width: 140,
      align: 'center',
      defaultVisible: true,
      sortType: 'number',
    },
    {
      title: 'Validity (months)',
      dataIndex: 'validity_months',
      key: 'validity_months',
      width: 160,
      align: 'center',
      defaultVisible: true,
      sortType: 'number',
    },
    {
      title: 'Assessment',
      dataIndex: 'requires_assessment',
      key: 'requires_assessment',
      width: 140,
      align: 'center',
      defaultVisible: false,
      sortType: 'boolean',
      render: (requires) => (
        <Tag color={requires ? 'green' : 'default'}>{requires ? 'Required' : 'Not Required'}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 110,
      align: 'center',
      defaultVisible: true,
      sortType: 'boolean',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>{active ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 200,
      align: 'center',
      defaultVisible: true,
      render: (_, record) => (
        <Space>
          <Button
            icon={<Icons.Pencil className="w-3.5 h-3.5 text-amber-600" />}
            size="small"
            aria-label={`Edit ${record.course_name}`}
            onClick={() => handleEditCourse(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this course?"
            onConfirm={() => handleDeleteCourse(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<Icons.Trash2 className="w-3.5 h-3.5" />} size="small" aria-label={`Delete ${record.course_name}`}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Operation Requirements Table Columns
  const allRequirementColumns = [
    {
      title: 'Operation Code',
      dataIndex: 'operation_code',
      key: 'operation_code',
      width: 200,
      defaultVisible: true,
      sortType: 'string',
      searchable: true,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Operation Name',
      dataIndex: 'operation_name',
      key: 'operation_name',
      width: 320,
      defaultVisible: true,
      sortType: 'string',
      searchable: true,
    },
    {
      title: 'Required Course',
      dataIndex: 'course_name',
      key: 'course_name',
      width: 320,
      defaultVisible: true,
      sortType: 'string',
      searchable: true,
    },
    {
      title: 'Course Code',
      dataIndex: 'course_code',
      key: 'course_code',
      width: 160,
      defaultVisible: false,
      sortType: 'string',
      searchable: true,
    },
    {
      title: 'Mandatory',
      dataIndex: 'is_mandatory',
      key: 'is_mandatory',
      width: 140,
      align: 'center',
      defaultVisible: true,
      sortType: 'boolean',
      render: (mandatory) => (
        <Tag color={mandatory ? 'red' : 'default'}>{mandatory ? 'Mandatory' : 'Optional'}</Tag>
      ),
    },
  ];

  return (
    <div
      className="p-2.5 pt-5"
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
    >
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        classNames={{ body: 'page-card-scroll' }}
        style={{
          backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff',
          height: 'calc(100dvh - 120px)',
          display: 'flex',
          flexDirection: 'column',
        }}
        styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' } }}
        title={
          <PageHeader
            compact
            icon={<Icons.BookOpen size={22} />}
            title={t('Training Configuration')}
            description="Training courses and the qualifications each operation type requires."
            primary={
              activeTab === '1'
                ? { label: 'Add Training Course', icon: <PlusOutlined />, onClick: handleAddCourse }
                : { label: 'Add Operation Requirement', icon: <PlusOutlined />, onClick: handleAddRequirement }
            }
          />
        }
      >
      {/* Top Summary Cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={8}>
          <StatCard
            label="Total Courses"
            value={stats.totalCourses}
            icon={<BookOpen size={24} />}
            bgColor={`${defaultColors.primary}22`}
            iconColor={defaultColors.primary}
          />
        </Col>
        <Col xs={24} sm={8}>
          <StatCard
            label="Active Courses"
            value={stats.activeCourses}
            icon={<CheckCircle size={24} />}
            bgColor={`${defaultColors.success}22`}
            iconColor={defaultColors.success}
          />
        </Col>
        <Col xs={24} sm={8}>
          <StatCard
            label="Operations w/ Requirements"
            value={stats.operationsWithRequirements}
            icon={<Settings size={24} />}
            bgColor={`${defaultColors.info}22`}
            iconColor={defaultColors.info}
          />
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab} size="large" tabBarStyle={{ backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f9f9f9', borderRadius: '8px', padding: '2px 8px' }}>
        {/* Training Courses Tab */}
        <Tabs.TabPane
          tab={
            <span>
              <BookOutlined style={{ marginRight: 8 }} />
              Training Courses
            </span>
          }
          key="1"
        >
          <DataTable
            className="training-courses-table"
            tableKey="training_courses"
            searchable
            searchPlaceholder="Search courses..."
            columnConfig
            showCount={false}
            columns={allCourseColumns}
            data={courses || []}
            rowKey="id"
            loading={coursesLoading}
            locale={{ emptyText: <EmptyState title="No training courses yet" description="Add a course to start tracking operator qualifications." actionLabel="Add Training Course" onAction={handleAddCourse} /> }}
            bordered
            scrollX="max-content"
            height={null}
          />
        </Tabs.TabPane>

        {/* Operation Requirements Tab */}
        <Tabs.TabPane
          tab={
            <span>
              <ToolOutlined style={{ marginRight: 8 }} />
              Operation Requirements
            </span>
          }
          key="2"
        >
          <DataTable
            className="operation-requirements-table"
            tableKey="operation_requirements"
            searchable
            searchPlaceholder="Search requirements..."
            columnConfig
            showCount={false}
            columns={allRequirementColumns}
            data={requirements || []}
            rowKey="id"
            loading={requirementsLoading}
            locale={{ emptyText: <EmptyState title="No operation requirements yet" description="Link a course to an operation type to require it before execution." actionLabel="Add Operation Requirement" onAction={handleAddRequirement} /> }}
            bordered
            scrollX="max-content"
            height={null}
          />
        </Tabs.TabPane>
      </Tabs>

      {/* Course Modal */}
      <PageModal
        open={courseModalVisible}
        icon={<BookOutlined style={{ fontSize: 22 }} />}
        title={editingCourse ? 'Edit Training Course' : 'Create Training Course'}
        description="A course an operator completes to qualify for an operation."
        onCancel={() => {
          setCourseModalVisible(false);
          courseForm.resetFields();
          setEditingCourse(null);
        }}
        width={700}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => {
                setCourseModalVisible(false);
                courseForm.resetFields();
                setEditingCourse(null);
              }}
            >
              Cancel
            </Button>
            <Button type="primary" onClick={handleSaveCourse}>
              {editingCourse ? 'Update' : 'Create'}
            </Button>
          </div>
        }
      >
        <Form form={courseForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="course_code"
                label="Course Code"
                rules={[{ required: true, message: 'Please enter course code' }]}
              >
                <Input placeholder="e.g., GMP-001" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="training_type"
                label="Training Type"
                rules={[{ required: true, message: 'Please select training type' }]}
              >
                <Select placeholder="Select training type">
                  {trainingTypes.map((type) => (
                    <Option key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="course_name"
            label="Course Name"
            rules={[{ required: true, message: 'Please enter course name' }]}
          >
            <Input placeholder="e.g., Good Manufacturing Practice Fundamentals" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Brief description of the training course" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="duration_hours"
                label="Duration (hours)"
                rules={[{ required: true, message: 'Please enter duration' }]}
              >
                <InputNumber min={0.5} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="validity_months"
                label="Validity (months)"
                rules={[{ required: true, message: 'Please enter validity period' }]}
                initialValue={12}
              >
                <InputNumber min={1} max={60} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="passing_score" label="Passing Score (%)" initialValue={80}>
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="requires_assessment"
                label="Requires Assessment"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_active"
                label="Active Status"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="training_material_url" label="Training Material URL">
            <Input placeholder="https://..." />
          </Form.Item>
        </Form>
      </PageModal>

      {/* Operation Requirement Modal */}
      <PageModal
        open={requirementModalVisible}
        icon={<ToolOutlined style={{ fontSize: 22 }} />}
        title="Add Operation Training Requirement"
        description="Require a course before an operator can run this operation."
        onCancel={() => {
          setRequirementModalVisible(false);
          requirementForm.resetFields();
        }}
        width={600}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => {
                setRequirementModalVisible(false);
                requirementForm.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button type="primary" onClick={handleSaveRequirement}>
              Add
            </Button>
          </div>
        }
      >
        <Form form={requirementForm} layout="vertical">
          <Form.Item
            name="operation_code"
            label="Operation"
            rules={[{ required: true, message: 'Please select an operation' }]}
          >
            <Select
              placeholder={operations.length === 0 ? 'No operations available' : 'Select operation'}
              showSearch
              optionFilterProp="label"
              disabled={operations.length === 0}
            >
              {operations.map((op) => (
                <Option
                  key={op.id || op.operation_code}
                  value={op.operation_code}
                  label={op.operation_name}
                >
                  {op.operation_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="course_id"
            label="Required Training Course"
            rules={[{ required: true, message: 'Please select a training course' }]}
          >
            <Select
              placeholder={courses.filter((c) => c.is_active).length === 0 ? 'No active courses available' : 'Select training course'}
              showSearch
              optionFilterProp="label"
              disabled={courses.filter((c) => c.is_active).length === 0}
            >
              {courses
                .filter((c) => c.is_active)
                .map((course) => (
                  <Option
                    key={course.id}
                    value={course.id}
                    label={`${course.course_code} - ${course.course_name}`}
                  >
                    {course.course_code} - {course.course_name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="is_mandatory"
            label="Is Mandatory"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Mandatory" unCheckedChildren="Optional" />
          </Form.Item>
        </Form>
      </PageModal>
      </Card>
    </div>
  );
};

export default TrainingConfiguration;
